import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { UfSelect } from "@/shared/components/UfSelect";
import { Spinner } from "@/shared/components/ui/spinner";
import { Search, X, Plus, ChevronLeft } from "lucide-react";
import { GenderType, PersonType } from "@/shared/types/enums";
import {
  formatDocument,
  formatPhone,
  formatCEP,
} from "@/shared/lib/formatters";
import type { NewClientData } from "../../schemas";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { normalizeDocumentInput } from "@/shared/lib/document";
import { useDebouncedSearch } from "@/shared/hooks/useDebouncedSearch";

export interface ContractOption {
  id: number;
  contractNumber: string;
  clientName: string;
  clientId: number;
}

export interface ClientOption {
  id: number;
  name: string;
  document: string;
}

interface ContractSelectorProps {
  selectedId: number | null;
  selectedDisplay: string;
  onSelect: (
    id: number,
    display: string,
    client: { id: number; name: string },
  ) => void;
  onClear: () => void;
  searchContracts: (
    term: string,
    signal?: AbortSignal,
  ) => Promise<ContractOption[]>;
  createContract: (
    contractNumber: string,
    clientId: number,
  ) => Promise<{ id: number; contractNumber: string }>;
  searchClients: (
    term: string,
    signal?: AbortSignal,
  ) => Promise<ClientOption[]>;
  createClient: (data: NewClientData) => Promise<{ id: number; name: string }>;
}

type Mode = "search" | "creating";
type ClientMode = "search" | "creating";

const EMPTY_CLIENT: Partial<NewClientData> = {
  personType: PersonType.FISICA,
  gender: GenderType.MALE,
  state: "DF",
};

export const ContractSelector = ({
  selectedId,
  selectedDisplay,
  onSelect,
  onClear,
  searchContracts,
  createContract,
  searchClients,
  createClient,
}: ContractSelectorProps) => {
  const [mode, setMode] = useState<Mode>("search");
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<ContractOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [contractNumber, setContractNumber] = useState("");
  const [clientMode, setClientMode] = useState<ClientMode>("search");
  const [clientTerm, setClientTerm] = useState("");
  const [clientResults, setClientResults] = useState<ClientOption[]>([]);
  const [clientSearching, setClientSearching] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [selectedClientName, setSelectedClientName] = useState("");
  const [newClient, setNewClient] =
    useState<Partial<NewClientData>>(EMPTY_CLIENT);
  const [saving, setSaving] = useState(false);
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Latest-ref pattern: as callbacks vindas do useRentalWizardSearch são
  // estáveis via useCallback, mas o dono da prop pode trocá-las entre
  // renders sem que a busca precise ser reagendada.
  const searchContractsRef = useRef(searchContracts);
  const searchClientsRef = useRef(searchClients);
  useEffect(() => {
    searchContractsRef.current = searchContracts;
    searchClientsRef.current = searchClients;
  });

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setClientOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const contractFetcher = useCallback(
    (t: string, signal: AbortSignal) => searchContractsRef.current(t, signal),
    [],
  );
  useDebouncedSearch({
    term,
    fetcher: contractFetcher,
    setItems: setResults,
    setLoading: setSearching,
    onError: (e) =>
      toast.error(getApiErrorMessage(e, "Erro ao buscar contratos")),
  });

  const clientFetcher = useCallback(
    (t: string, signal: AbortSignal) => searchClientsRef.current(t, signal),
    [],
  );
  useDebouncedSearch({
    term: clientTerm,
    fetcher: clientFetcher,
    setItems: setClientResults,
    setLoading: setClientSearching,
    onError: (e) =>
      toast.error(getApiErrorMessage(e, "Erro ao buscar clientes")),
  });

  const handleCreateContract = useCallback(async () => {
    if (!contractNumber.trim() || !selectedClientId) return;
    setSaving(true);
    try {
      const contract = await createContract(contractNumber, selectedClientId);
      if (!isMountedRef.current) return;
      onSelect(contract.id, contract.contractNumber, {
        id: selectedClientId,
        name: selectedClientName,
      });
      setMode("search");
      setContractNumber("");
      setSelectedClientId(null);
      setSelectedClientName("");
      setClientMode("search");
      toast.success(`Contrato ${contract.contractNumber} criado com sucesso!`);
    } catch (error: unknown) {
      if (!isMountedRef.current) return;
      toast.error(getApiErrorMessage(error, "Erro ao criar contrato"));
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }, [
    contractNumber,
    selectedClientId,
    selectedClientName,
    createContract,
    onSelect,
  ]);

  const handleCreateClient = useCallback(async () => {
    if (
      !newClient.name ||
      !newClient.document ||
      !newClient.phone ||
      !newClient.complement
    )
      return;
    setSaving(true);
    try {
      const isJuridica = newClient.personType === PersonType.JURIDICA;
      const client = await createClient({
        ...EMPTY_CLIENT,
        ...newClient,
        gender: isJuridica ? null : (newClient.gender ?? GenderType.MALE),
      } as NewClientData);
      if (!isMountedRef.current) return;
      setSelectedClientId(client.id);
      setSelectedClientName(client.name);
      setClientMode("search");
      setClientTerm(client.name);
      setNewClient(EMPTY_CLIENT);
      toast.success(`Cliente ${client.name} criado com sucesso!`);
    } catch (error: unknown) {
      if (!isMountedRef.current) return;
      toast.error(getApiErrorMessage(error, "Erro ao criar cliente"));
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }, [newClient, createClient]);

  const setDocumentField = (raw: string) => {
    const document = normalizeDocumentInput(
      raw,
      newClient.personType === PersonType.JURIDICA,
    );
    setNewClient((p) => ({ ...p, document }));
  };

  const setPhoneField = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 11);
    setNewClient((p) => ({ ...p, phone: digits }));
  };

  const setPostalCodeField = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    setNewClient((p) => ({ ...p, postalCode: digits }));
  };

  const displayDocument = (v = "") => formatDocument(v);

  const displayPhone = (v = "") => {
    const d = v.replace(/\D/g, "");
    if (d.length >= 10) return formatPhone(d);
    return v;
  };

  const displayPostalCode = (v = "") => {
    const d = v.replace(/\D/g, "");
    if (d.length === 8) return formatCEP(d);
    return v;
  };

  if (selectedId) {
    return (
      <div className="flex items-center gap-2 p-2 rounded-md border bg-muted/30">
        <span className="text-sm font-medium flex-1">{selectedDisplay}</span>
        <button
          type="button"
          onClick={onClear}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Remover contrato"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (mode === "creating") {
    return (
      <div ref={containerRef} className="space-y-3 p-3 rounded-md border">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setMode("search")}
            className="text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium">Novo contrato</span>
        </div>
        <Input
          placeholder="Número do contrato *"
          value={contractNumber}
          onChange={(e) => setContractNumber(e.target.value)}
        />
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Cliente *</p>
          {selectedClientId ? (
            <div className="flex items-center gap-2 p-2 rounded border bg-muted/30">
              <span className="text-sm flex-1">{selectedClientName}</span>
              <button
                type="button"
                onClick={() => {
                  setSelectedClientId(null);
                  setSelectedClientName("");
                  setClientTerm("");
                }}
                aria-label="Remover cliente"
              >
                <X className="w-3 h-3 text-muted-foreground" />
              </button>
            </div>
          ) : clientMode === "creating" ? (
            <div className="space-y-2 p-2 rounded border">
              {(() => {
                const isJuridica = newClient.personType === PersonType.JURIDICA;
                return (
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      className="col-span-2 h-10 rounded-md border border-input bg-background px-3 text-sm"
                      value={newClient.personType ?? PersonType.FISICA}
                      onChange={(e) => {
                        const value = e.target.value as PersonType;
                        setNewClient((p) => ({
                          ...p,
                          personType: value,
                          gender:
                            value === PersonType.JURIDICA
                              ? null
                              : (p.gender ?? GenderType.MALE),
                        }));
                      }}
                    >
                      <option value={PersonType.FISICA}>
                        Pessoa Física (CPF)
                      </option>
                      <option value={PersonType.JURIDICA}>
                        Pessoa Jurídica (CNPJ)
                      </option>
                    </select>
                    <Input
                      placeholder={isJuridica ? "Razão Social *" : "Nome *"}
                      maxLength={150}
                      value={newClient.name ?? ""}
                      onChange={(e) =>
                        setNewClient((p) => ({ ...p, name: e.target.value }))
                      }
                    />
                    <Input
                      placeholder={isJuridica ? "CNPJ *" : "CPF *"}
                      value={displayDocument(newClient.document)}
                      onChange={(e) => setDocumentField(e.target.value)}
                      maxLength={18}
                    />
                    <Input
                      placeholder="Telefone *"
                      value={displayPhone(newClient.phone)}
                      onChange={(e) => setPhoneField(e.target.value)}
                      maxLength={15}
                    />
                    {!isJuridica && (
                      <select
                        className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                        value={newClient.gender ?? GenderType.MALE}
                        onChange={(e) =>
                          setNewClient((p) => ({
                            ...p,
                            gender: e.target.value as GenderType,
                          }))
                        }
                      >
                        <option value={GenderType.MALE}>Masculino</option>
                        <option value={GenderType.FEMALE}>Feminino</option>
                      </select>
                    )}
                    <Input
                      placeholder="Email"
                      value={newClient.email ?? ""}
                      onChange={(e) =>
                        setNewClient((p) => ({ ...p, email: e.target.value }))
                      }
                      className="col-span-2"
                    />
                    <Input
                      placeholder="Rua *"
                      value={newClient.street ?? ""}
                      onChange={(e) =>
                        setNewClient((p) => ({ ...p, street: e.target.value }))
                      }
                      className="col-span-2"
                    />
                    <Input
                      placeholder="Número *"
                      value={newClient.number ?? ""}
                      maxLength={10}
                      onChange={(e) =>
                        setNewClient((p) => ({ ...p, number: e.target.value }))
                      }
                    />
                    <Input
                      placeholder="Complemento *"
                      value={newClient.complement ?? ""}
                      onChange={(e) =>
                        setNewClient((p) => ({
                          ...p,
                          complement: e.target.value,
                        }))
                      }
                    />
                    <Input
                      placeholder="Bairro *"
                      value={newClient.neighborhood ?? ""}
                      onChange={(e) =>
                        setNewClient((p) => ({
                          ...p,
                          neighborhood: e.target.value,
                        }))
                      }
                    />
                    <Input
                      placeholder="CEP *"
                      value={displayPostalCode(newClient.postalCode)}
                      onChange={(e) => setPostalCodeField(e.target.value)}
                      maxLength={9}
                    />
                    <UfSelect
                      value={newClient.state ?? "DF"}
                      onChange={(v) =>
                        setNewClient((p) => ({ ...p, state: v }))
                      }
                    />
                  </div>
                );
              })()}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  type="button"
                  onClick={handleCreateClient}
                  disabled={
                    saving ||
                    !newClient.name ||
                    !newClient.document ||
                    !newClient.phone ||
                    !newClient.complement
                  }
                >
                  {saving ? <Spinner className="w-3 h-3 mr-1" /> : null} Criar
                  cliente
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  type="button"
                  onClick={() => {
                    setClientMode("search");
                    setNewClient(EMPTY_CLIENT);
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Buscar cliente..."
                  value={clientTerm}
                  onChange={(e) => {
                    setClientTerm(e.target.value);
                    setClientOpen(true);
                  }}
                  onFocus={() =>
                    clientResults.length > 0 && setClientOpen(true)
                  }
                  className="pl-9"
                />
                {clientOpen &&
                  (clientSearching ||
                    clientResults.length > 0 ||
                    clientTerm) && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-background shadow-md">
                      {clientSearching ? (
                        <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                          <Spinner className="w-4 h-4" /> Buscando...
                        </div>
                      ) : clientResults.length === 0 ? (
                        <p className="px-3 py-2 text-sm text-muted-foreground">
                          Nenhum cliente encontrado
                        </p>
                      ) : (
                        <ul className="max-h-40 overflow-y-auto py-1">
                          {clientResults.map((c) => (
                            <li key={c.id}>
                              <button
                                type="button"
                                className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
                                onClick={() => {
                                  setSelectedClientId(c.id);
                                  setSelectedClientName(c.name);
                                  setClientOpen(false);
                                  setClientTerm(c.name);
                                }}
                              >
                                <span className="font-medium">{c.name}</span>
                                <span className="ml-2 text-xs text-muted-foreground">
                                  {c.document}
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full gap-1.5"
                onClick={() => {
                  setClientMode("creating");
                  setNewClient(EMPTY_CLIENT);
                }}
              >
                <Plus className="w-3.5 h-3.5" /> Criar novo cliente
              </Button>
            </div>
          )}
        </div>
        <Button
          size="sm"
          type="button"
          onClick={handleCreateContract}
          disabled={saving || !contractNumber.trim() || !selectedClientId}
          className="w-full"
        >
          {saving ? <Spinner className="w-3 h-3 mr-1" /> : null} Criar contrato
        </Button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          placeholder="Buscar por número ou ID..."
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => (results.length > 0 || searching) && setOpen(true)}
          className="pl-9 h-11"
        />
        {open && (searching || results.length > 0 || term) && (
          <div className="absolute z-50 mt-1 w-full rounded-md border bg-background shadow-md">
            {searching ? (
              <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                <Spinner className="w-4 h-4" /> Buscando...
              </div>
            ) : results.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">
                Nenhum contrato encontrado
              </p>
            ) : (
              <ul className="max-h-48 overflow-y-auto py-1">
                {results.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
                      onClick={() => {
                        onSelect(c.id, c.contractNumber, {
                          id: c.clientId,
                          name: c.clientName,
                        });
                        setOpen(false);
                        setTerm("");
                      }}
                    >
                      <span className="font-medium">{c.contractNumber}</span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        {c.clientName}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full gap-1.5"
        onClick={() => {
          setMode("creating");
          setOpen(false);
        }}
      >
        <Plus className="w-3.5 h-3.5" /> Criar novo contrato
      </Button>
    </div>
  );
};
